import React, { ReactNode } from 'react'
import { List } from 'antd'
import './GridList.styles.css'

export interface GridListProps<T> {
  dataSource: T[]
  renderItem: (wrappedItem: { originalItem: T, gridIndex: number, key: string }, index: number) => ReactNode
  loading?: boolean
  loadingItemsCount?: number
  gutter?: number
  grid?: {
    xs?: number
    sm?: number
    md?: number
    lg?: number
    xl?: number
    xxl?: number
  }
  className?: string
  emptyComponent?: ReactNode
}

const defaultGrid = {
  xs: 1,
  sm: 1,
  md: 1,
  lg: 2,
  xl: 3,
  xxl: 4,
}

const GridList = <T,>({
  dataSource,
  renderItem,
  loading = false,
  loadingItemsCount = 6,
  gutter = 32,
  grid = defaultGrid,
  className = '',
  emptyComponent
}: GridListProps<T>) => {
  if (loading) {
    return (
      <div className={`grid-list-container ${className}`}>
        <List 
          grid={{ gutter, ...grid }}
          dataSource={Array.from({ length: loadingItemsCount }, (_, index) => ({ key: `loading-${index}` }))}
          renderItem={(item, index) => (
            <List.Item key={item.key} className="grid-list-item">
              <div className="grid-list-skeleton-card">
                <div className="grid-list-skeleton-header">
                  <div className="grid-list-skeleton-title" />
                  <div className="grid-list-skeleton-tag" />
                </div>
                <div className="grid-list-skeleton-content" />
                <div className="grid-list-skeleton-actions">
                  <div className="grid-list-skeleton-button" />
                  <div className="grid-list-skeleton-button" />
                  <div className="grid-list-skeleton-button" />
                </div>
              </div>
            </List.Item>
          )}
        />
      </div>
    )
  }

  if (dataSource.length === 0 && emptyComponent) {
    return (
      <div className={`grid-list-container ${className}`}>
        {emptyComponent}
      </div>
    )
  }

  const validItems = dataSource
    .filter(item => {
      return item && 
             typeof item === 'object' && 
             (item as any).uuid && 
             typeof (item as any).uuid === 'string' && 
             (item as any).uuid.length > 0
    })
    .map((item, index) => ({
      originalItem: item,
      gridIndex: index,
      key: (item as any).uuid
    }))
  
  return (
    <div className={`grid-list-container ${className}`}>
      <List
        grid={{ gutter, ...grid }}
        dataSource={validItems}
        renderItem={(wrappedItem, index) => (
          <List.Item key={wrappedItem.key} className="grid-list-item">
            {renderItem(wrappedItem, index)}
          </List.Item>
        )}
      />
    </div>
  )
}

export default GridList
